import React, { createContext, useContext } from "react";
import * as XLSX from "xlsx";
import api from "@/lib/api";
import { Loader2, FileDown, FileSpreadsheet, Printer, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";

// ---------- Theme ----------
export const CHART_COLORS = ["#b81f24", "#0f766e", "#b45309", "#1d4ed8", "#4d7c0f", "#be185d", "#0369a1"];
export const BAND = {
  green: { bg: "#15803d", soft: "#dcfce7", text: "#166534", label: "Strong" },
  amber: { bg: "#b45309", soft: "#fef3c7", text: "#92400e", label: "Developing" },
  red: { bg: "#b91c1c", soft: "#fee2e2", text: "#991b1b", label: "Needs work" },
  na: { bg: "#a8a29e", soft: "#f5f5f4", text: "#78716c", label: "No data" },
};
export function bandOf(v) {
  if (v === null || v === undefined) return "na";
  if (v >= 80) return "green";
  if (v >= 65) return "amber";
  return "red";
}

export const RISK_LABELS = {
  low_improvement: "Low improvement",
  repeated_failure: "Repeated failure",
  low_accountability: "Low accountability",
  conflict_avoidance: "Conflict avoidance",
  low_empathy: "Low empathy",
  low_engagement: "Low engagement",
};

// ---------- Filters context ----------
export const FiltersContext = createContext(null);
export const useFilters = () => useContext(FiltersContext);

export function buildQuery(filters) {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([k, v]) => {
    if (v && v !== "all") params.append(k, v);
  });
  const s = params.toString();
  return s ? `?${s}` : "";
}

// ---------- Export helpers ----------
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function exportCSV(rows, filename) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
}

export function exportExcel(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) => {
    const ws = XLSX.utils.json_to_sheet(s.rows.length ? s.rows : [{}]);
    XLSX.utils.book_append_sheet(wb, ws, s.name.slice(0, 31));
  });
  XLSX.writeFile(wb, filename);
}

export async function downloadPdf(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  triggerDownload(res.data, filename);
}

export function ExportBar({ onExcel, onCSV, onPdf, pdfLabel = "PDF" }) {
  return (
    <div className="flex items-center gap-2" data-testid="export-bar">
      {onPdf && (
        <Button variant="outline" size="sm" onClick={onPdf} data-testid="export-pdf-btn"
          className="rounded-md border-stone-300 text-stone-700 hover:bg-stone-100">
          <FileDown className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> {pdfLabel}
        </Button>
      )}
      {onExcel && (
        <Button variant="outline" size="sm" onClick={onExcel} data-testid="export-excel-btn"
          className="rounded-md border-stone-300 text-stone-700 hover:bg-stone-100">
          <FileSpreadsheet className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Excel
        </Button>
      )}
      {onCSV && (
        <Button variant="outline" size="sm" onClick={onCSV} data-testid="export-csv-btn"
          className="rounded-md border-stone-300 text-stone-700 hover:bg-stone-100">
          CSV
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn"
        className="rounded-md border-stone-300 text-stone-700 hover:bg-stone-100">
        <Printer className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Print
      </Button>
    </div>
  );
}

// ---------- UI atoms ----------
export function Panel({ title, subtitle, right, children, className = "", testid }) {
  return (
    <div data-testid={testid}
      className={`bg-white rounded-2xl border border-stone-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 sm:p-6 ${className}`}>
      {(title || right) && (
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            {title && <h3 className="font-display text-base font-semibold text-stone-900 tracking-tight">{title}</h3>}
            {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function KpiCard({ label, value, sub, icon: Icon, accent = "#b81f24", testid }) {
  return (
    <div data-testid={testid}
      className="bg-white rounded-2xl border border-stone-200/80 p-5 transition-all hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.12em] text-stone-500 font-medium">{label}</span>
        {Icon && (
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}14` }}>
            <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: accent }} />
          </span>
        )}
      </div>
      <div className="mt-3 font-display text-3xl font-semibold text-stone-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

export function ScoreBadge({ value }) {
  const b = BAND[bandOf(value)];
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums"
      style={{ backgroundColor: b.soft, color: b.text }}>
      {value === null || value === undefined ? "—" : value}
    </span>
  );
}

export function BandDot({ value }) {
  const b = BAND[bandOf(value)];
  return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.bg }} />;
}

export function HeatCell({ value }) {
  const b = BAND[bandOf(value)];
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg justify-between" style={{ backgroundColor: b.soft }}>
      <span className="text-sm font-semibold tabular-nums" style={{ color: b.text }}>
        {value === null || value === undefined ? "—" : value}
      </span>
      <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: b.text }}>{b.label}</span>
    </div>
  );
}

export function RiskBadge({ flag }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-red-50 text-red-700 border border-red-100"
      data-testid={`risk-${flag}`}>
      {RISK_LABELS[flag] || flag}
    </span>
  );
}

export function Delta({ value }) {
  if (value === null || value === undefined) return <span className="text-stone-400">—</span>;
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center text-xs font-semibold ${up ? "text-emerald-600" : "text-red-600"}`}>
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(value)}
    </span>
  );
}

export function Loading({ label = "Loading analytics…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-stone-500" data-testid="analytics-loading">
      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> {label}
    </div>
  );
}

export function Empty({ label = "No data for the selected filters." }) {
  return (
    <div className="py-16 text-center text-sm text-stone-500" data-testid="analytics-empty">{label}</div>
  );
}

export function PageHeader({ title, description, right }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">{title}</h1>
        {description && <p className="text-sm text-stone-500 mt-1.5 max-w-2xl">{description}</p>}
      </div>
      {right}
    </div>
  );
}
